<template>
    <div>
        <!-- Section header. The icon is its own column so the title and sub-copy share a left edge. -->
        <div class="mb-4 flex items-start gap-2">
            <i v-if="selfTitle" class="bi bi-person-fill text-sm leading-6 text-gray-900 shrink-0" aria-hidden="true"></i>
            <div class="min-w-0 flex-1">
                <!-- Title with the due date to the side. -->
                <div class="flex items-center justify-between gap-2">
                    <h2 class="scaffold-body truncate">{{ selfTitle }}</h2>
                    <span v-if="dateLabel" class="scaffold-body-small text-valence-grey-750 whitespace-nowrap shrink-0">{{ dateLabel }}</span>
                </div>
                <!-- Optional sub-copy below the title. -->
                <p v-if="selfSubtitle" class="scaffold-body-small text-valence-grey-750 mt-1 whitespace-pre-line">{{ selfSubtitle }}</p>
            </div>
        </div>
        <!-- Review rows (no accordion — the self section always shows its rows directly) -->
        <div class="rounded-xl flex flex-col bg-white border border-valence-grey-200">
            <PerformanceManagementCTA
                v-for="review in reviews"
                :key="review.id"
                :review="review"
                class="px-5"
                :is-loading="!!currentReviewId && currentReviewId === review.id"
                :is-disabled="!!currentReviewId && currentReviewId !== review.id"
                :is-moment-read-only="isMomentReadOnly"
                :is-coming-soon="isSectionLocked"
                @cta-convo-clicked="(reviewId) => $emit('cta-convo-clicked', reviewId)"
                @completion-toggled="(reviewId, complete) => $emit('completion-toggled', reviewId, complete)" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { formatSectionDateLabel, isStartDateInFuture } from "~vue/composables/useCtaDateRange";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed } from "vue";

import PerformanceManagementCTA from "./PerformanceManagementCTA.vue";

const props = withDefaults(
    defineProps<{
        dueDate?: string | null;
        startDate?: string | null;
        selfTitle?: string;
        selfSubtitle?: string;
        reviews: DecoratedPerformanceReview[];
        currentReviewId?: string | null;
        isMomentReadOnly?: boolean;
    }>(),
    {
        selfTitle: "",
        selfSubtitle: "",
        currentReviewId: null,
    },
);

defineEmits(["cta-convo-clicked", "completion-toggled"]);

const dateLabel = computed(() => formatSectionDateLabel(props.startDate, props.dueDate));

// Section hasn't opened yet — disable/grey its CTAs until the start date arrives.
const isSectionLocked = computed(() => isStartDateInFuture(props.startDate));
</script>
